import type { Position } from '../../../types/project';

const PREFIX = 'pa:draft:';

const VERSION = 1;

const TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface StoredDrafts {
    v: number;
    savedAt: number;
    positions: Position[];
    fileName?: string;
    region?: string;
}

export interface DraftsMeta {
    fileName?: string;
    region?: string;
}

const keyOf = (userId: string, projectId: string): string => `${PREFIX}${userId}:${projectId}`;

const safe = <T>(fn: () => T, fallback: T): T => {
    try {
        return fn();
    } catch {
        return fallback;
    }
};

const isExpired = (parsed: StoredDrafts): boolean =>
    parsed.v !== VERSION || Date.now() - parsed.savedAt > TTL_MS;

export const loadDrafts = (userId: string, projectId: string): StoredDrafts | null =>
    safe(() => {
        const key = keyOf(userId, projectId);
        const raw = localStorage.getItem(key);
        if (!raw) return null;

        const parsed = JSON.parse(raw) as StoredDrafts;

        if (!Array.isArray(parsed?.positions) || isExpired(parsed)) {
            localStorage.removeItem(key);
            return null;
        }

        return parsed;
    }, null);

export const saveDrafts = (
    userId: string,
    projectId: string,
    positions: Position[],
    meta: DraftsMeta = {},
): void =>
    safe(() => {
        const key = keyOf(userId, projectId);

        if (!positions.length) {
            localStorage.removeItem(key);
            return;
        }

        const payload: StoredDrafts = {
            v: VERSION,
            savedAt: Date.now(),
            positions,
            ...(meta.fileName ? { fileName: meta.fileName } : {}),
            ...(meta.region ? { region: meta.region } : {}),
        };

        localStorage.setItem(key, JSON.stringify(payload));
    }, undefined);